import React, { useState, useEffect, useRef, useCallback } from "react";

import classNames from "classnames";
import ReactPlayer from "react-player";
import WaveSurfer from "wavesurfer.js";

import { Dictionary } from "@reduxjs/toolkit";

import { saveWaveformData } from "~/api/saveWaveformData";
import {
  formatCloudinaryAudioMediaLink,
  getStreamingAudioURL,
  videoTime,
} from "~/components/classLanding/utils";

import { ActiveMediaPlayer } from "../../../ActiveMediaPlayer";
import * as styles from "./AudioPlayer.css";

interface AudioPlayerProps {
  src: string;
  darkTheme?: boolean;
  durationInSeconds?: string;
  projectInfo?: {
    waveformData?: string;
    projectMediaId?: string;
    projectId?: string;
    mediaStreamingURL?: string;
    isStreamingMedia?: boolean;
    processStreamingOnUpload?: boolean;
  };
  customStyles?: Dictionary<string>;
  waveform?: string;
}

const AudioPlayer = ({
  src,
  durationInSeconds,
  darkTheme,
  projectInfo,
  customStyles,
  waveform,
}: AudioPlayerProps): React.ReactElement => {
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const audioPlayerRef = useRef<ReactPlayer | null>(null);

  const mediaLink = formatCloudinaryAudioMediaLink(src);

  const [isPaused, setIsPaused] = useState(true);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState({
    playedTime: "0:00",
    playedTimeInSeconds: 0,
  });

  const onWaveFormReady = useCallback(
    async (waveSurfer: WaveSurfer) => {

      const waveformData = waveSurfer.exportPeaks({ channels: 2, maxLength: 100 });
      waveSurferRef.current = waveSurfer;
      if (projectInfo && projectInfo.projectMediaId && projectInfo.projectId) {
        // Saves the waveform data to the existing project
        const projectMediaId = projectInfo.projectMediaId ? projectInfo.projectMediaId : "";
        await saveWaveformData(
          projectInfo.projectId,
          projectMediaId,
          waveformData as unknown as string,
        );
      }
    },
    [projectInfo],
  );

  const loadWaveform = useCallback(
    (waveSurfer: WaveSurfer) => {
      if (waveform) {

        // Project has already been saved and waveform data exists in project
        const peaks = waveform.split(",").map((peak) => parseFloat(peak) as number);
        waveSurferRef.current = waveSurfer;

        waveSurfer.load(mediaLink, [peaks]);
      } else if (projectInfo && projectInfo.waveformData && projectInfo.waveformData.length > 0) {
        // Project has already been saved and waveform data exists in project
        const peaks = projectInfo.waveformData ? JSON.parse(projectInfo.waveformData) : [];
        waveSurferRef.current = waveSurfer;

        waveSurfer.load(mediaLink, peaks);
      } else {
        // Project hasn't been saved yet or no waveform exists on project
        waveSurfer.load(mediaLink);
        waveSurfer.on("ready", () => onWaveFormReady(waveSurfer));
      }
    },
    [mediaLink, onWaveFormReady, projectInfo],
  );

  const onPlayButtonClick = () => {
    setIsPaused((isPaused) => !isPaused);
  };

  const onAudioProgress = (info: { played: number; playedSeconds: number }) => {
    if (info.played <= 0) return;

    const playedProgress = Math.min(info.played, 1);
    waveSurferRef.current?.seekTo(playedProgress);

    setAudioCurrentTime({
      playedTime: videoTime(info.playedSeconds),
      playedTimeInSeconds: info.playedSeconds,
    });

    if (playedProgress >= 1) {
      setIsPaused(true);
      setAudioCurrentTime({
        playedTime: audioDuration ? videoTime(audioDuration) : videoTime(info.playedSeconds),
        playedTimeInSeconds: audioDuration ? audioDuration : info.playedSeconds,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // this is to counteract a bug, where the scrolling to a custom time is glitchy.
    if (!audioDuration) return;

    const toTime = Number(e.target.value);
    const percentRatio = toTime / audioDuration;
    const clampedPercentRatio = Math.max(0, Math.min(percentRatio, 1));

    waveSurferRef.current?.seekTo(clampedPercentRatio);
    audioPlayerRef.current?.seekTo(clampedPercentRatio, "fraction");

    setAudioCurrentTime({
      playedTime: videoTime(toTime.toString()),
      playedTimeInSeconds: toTime,
    });
  };

  const getPlaybackURL = () => {
    let audioURL = mediaLink;
    if (projectInfo?.isStreamingMedia && projectInfo?.mediaStreamingURL) {
      // if we have the media streaming url, use that one.
      audioURL = projectInfo.mediaStreamingURL;
    }
    if (!projectInfo?.processStreamingOnUpload) {
      // if we haven't set to processStreamingOnUpload (we're processing it on upload now),
      // then we need to transcode to streaming through url on Cloudinary.
      audioURL = projectInfo?.waveformData ? getStreamingAudioURL(audioURL) : audioURL;
    }
    return audioURL;
  };

  const audioURL = getPlaybackURL();

  useEffect(() => {

    if (!containerRef.current) return;

    const waveSurfer = WaveSurfer.create({
      container: containerRef.current as HTMLDivElement,
      progressColor: customStyles?.progressColor
        ? customStyles.progressColor
        : darkTheme
          ? "#4167D1"
          : "#FAE26A",
      waveColor: customStyles?.waveColor || "#CDD4DE",
      height: 30,
      cursorWidth: 0,
      barGap: 2,
      barWidth: 3,
      // // barMinHeight: 20,
      barRadius: 10,
      // // pixelRatio: 4,
      interact: false,
      normalize: true,
      // responsive: true,
      // // partialRender: true,
      fillParent: true,
    });

    loadWaveform(waveSurfer);

    return () => {
      waveSurfer.destroy();
    };
  }, []);

  return (
    <div className={styles.audioPlayer} style={customStyles}>
      <div className={styles.audioPlayerControls}>
        <div className={styles.contentContainer}>
          {/* Using a Div element instead of ClickableContentWrapper, since it's causing the page to refresh */}
          <div
            style={
              customStyles?.playButtonBackground
                ? { background: customStyles.playButtonBackground }
                : {}
            }
            className={classNames(darkTheme && styles.playButtonDark, styles.playButton)}
            onClick={onPlayButtonClick}
          >
            <div
              style={
                customStyles?.playButtonBorder ? { borderColor: customStyles.playButtonBorder } : {}
              }
              className={
                isPaused
                  ? styles.videoPlayButton
                  : classNames(styles.videoPlayButton, styles.videoPausedButton)
              }
            />
          </div>
        </div>
      </div>
      <p className={classNames(darkTheme && styles.audioTimeDark, styles.audioTime)}>
        {audioCurrentTime.playedTime}
      </p>
      <div
        className={styles.waveContainer}
        style={customStyles?.waveContainerWidth ? { width: customStyles.waveContainerWidth } : {}}
      >
        <ActiveMediaPlayer
          url={audioURL}
          width="0px"
          height="0px"
          ref={(player) => {
            audioPlayerRef.current = player;
          }}
          playsinline={true}
          progressInterval={120}
          playing={!isPaused}
          onPause={() => setIsPaused(true)}
          onInterrupt={() => setIsPaused(true)}
          onProgress={onAudioProgress}
          onDuration={(duration: number) => setAudioDuration(duration)}
          config={{
            file: {
              hlsVersion: "1.0.1",
              hlsOptions: { autoStartLoad: true },
            },
          }}
        />
        <div className={styles.waveformWidth}>
          <div ref={containerRef} className={styles.waveformContainer} />
        </div>

        <input
          type="range"
          className={styles.audioControlsContainer}
          onChange={handleInputChange}
          value={audioCurrentTime.playedTimeInSeconds}
          step={0.1}
          min={0}
          max={audioDuration}
        />
      </div>
      <p className={classNames(darkTheme && styles.audioTimeDark, styles.audioTime)}>
        {videoTime(durationInSeconds || audioDuration)}
      </p>
    </div>
  );
};

export default AudioPlayer;
